/**
 * Wilo (wilotab.com) — featured product block with an animated "tabs organize themselves" visual.
 */
import React, {useEffect, useRef, useState} from 'react';
import {animate, LayoutGroup, motion, useInView, useReducedMotion} from "framer-motion";
import {wiloProduct} from "./data";
import {ScrollGrow, TiltCard, Magnetic} from "./motion";

const PROJECTS = [
    {id: "research", name: "Research", color: "#0a0a0a"},
    {id: "client", name: "Client X", color: "#525252"},
    {id: "docs", name: "Docs", color: "#a3a3a3"}
];

const TABS = [
    {id: "t1", title: "arXiv: attention", p: "research"},
    {id: "t2", title: "Client X roadmap", p: "client"},
    {id: "t3", title: "API v2 reference", p: "docs"},
    {id: "t4", title: "Inbox (42)", p: "idle"},
    {id: "t5", title: "Survey results", p: "research"},
    {id: "t6", title: "PR #482 review", p: "client"},
    {id: "t7", title: "Weather", p: "idle"},
    {id: "t8", title: "Style guide", p: "docs"},
    {id: "t9", title: "Lit review draft", p: "research"},
    {id: "t10", title: "Sprint board", p: "client"},
    {id: "t11", title: "News", p: "idle"},
    {id: "t12", title: "Onboarding spec", p: "docs"}
];

const colorOf = (p) => (PROJECTS.find((x) => x.id === p) || {}).color || "#8a93a3";

const MESSY_COUNT = 147;
const SORTED_COUNT = 12;
const STEP_MS = 3200;

function Chip({tab, small}) {
    return (
        <motion.div
            layoutId={tab.id}
            layout="position"
            className={"wx-wilo-chip" + (small ? " is-tucked" : "")}
            style={{"--c": colorOf(tab.p)}}
            transition={{type: "spring", stiffness: 260, damping: 28}}
        >
            <span className="wx-wilo-dot"/>
            <span className="wx-wilo-title">{tab.title}</span>
        </motion.div>
    );
}

function TabCounter({sorted, reduce}) {
    const [n, setN] = useState(reduce ? SORTED_COUNT : MESSY_COUNT);
    useEffect(() => {
        if (reduce) return undefined;
        const controls = animate(n, sorted ? SORTED_COUNT : MESSY_COUNT, {
            duration: sorted ? 1.1 : 0.6,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setN(Math.round(v))
        });
        return () => controls.stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorted, reduce]);
    return (
        <span className="wx-wilo-counter">
            <strong>{n}</strong> {sorted ? "tabs in view" : "open tabs"}
        </span>
    );
}

function WiloVisual() {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, {amount: 0.35});
    const [sorted, setSorted] = useState(!!reduce);

    useEffect(() => {
        if (reduce) {
            setSorted(true);
            return undefined;
        }
        if (!inView) return undefined;
        const id = setInterval(() => setSorted((s) => !s), STEP_MS);
        return () => clearInterval(id);
    }, [inView, reduce]);

    return (
        <div className="wx-wilo-visual" ref={ref} aria-hidden="true">
            <div className="wx-wilo-glow"/>
            <div className="wx-wilo-window">
                <div className="wx-wilo-bar">
                    <span className="wx-wilo-lights"><i/><i/><i/></span>
                    <span className="wx-wilo-brand">Wilo</span>
                    <TabCounter sorted={sorted} reduce={reduce}/>
                </div>
                <LayoutGroup>
                    <div className="wx-wilo-stage">
                        {sorted ? (
                            <>
                                <div className="wx-wilo-projects">
                                    {PROJECTS.map((p) => (
                                        <div className="wx-wilo-col" key={p.id} style={{"--c": p.color}}>
                                            <motion.div className="wx-wilo-col-head"
                                                        initial={{opacity: 0, y: -6}}
                                                        animate={{opacity: 1, y: 0}}
                                                        transition={{delay: 0.25}}>
                                                {p.name}
                                            </motion.div>
                                            {TABS.filter((t) => t.p === p.id).map((t) => <Chip key={t.id} tab={t}/>)}
                                        </div>
                                    ))}
                                </div>
                                <div className="wx-wilo-tray">
                                    <span className="wx-wilo-tray-label">Tucked away</span>
                                    {TABS.filter((t) => t.p === "idle").map((t) => <Chip key={t.id} tab={t} small/>)}
                                </div>
                            </>
                        ) : (
                            <div className="wx-wilo-strip">
                                {TABS.map((t) => <Chip key={t.id} tab={t}/>)}
                            </div>
                        )}
                    </div>
                </LayoutGroup>
            </div>
        </div>
    );
}

export default function WiloShowcase({withFeatures = false}) {
    const w = wiloProduct;
    return (
        <>
            <ScrollGrow className="wx-product-featured is-wilo" from={0.9}>
                <div className="wx-product-featured-body">
                    <div className="wx-product-tag">{w.tag}</div>
                    <h3>{w.name}: <span className="wx-grad-text">{w.tagline}</span></h3>
                    <p>{w.lead}</p>
                    <ul className="wx-product-points">
                        {w.proofPoints.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                    <Magnetic>
                        <a href={w.url} target="_blank" rel="noreferrer" className="wx-btn wx-btn-primary">
                            Try Wilo
                        </a>
                    </Magnetic>
                </div>
                <div className="wx-product-featured-visual is-wilo">
                    <WiloVisual/>
                </div>
            </ScrollGrow>

            {withFeatures && (
                <div className="wx-services-grid is-four">
                    {w.features.map((f, i) => (
                        <TiltCard key={f.name} className="wx-service-card" delay={(i % 4) * 0.08}>
                            <div className="wx-service-title">{f.name}</div>
                            <div className="wx-service-desc">{f.desc}</div>
                        </TiltCard>
                    ))}
                </div>
            )}
        </>
    );
}
